import React, { useState, useEffect } from 'react';
import { api } from '../App';
import BottomNav from '../components/BottomNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Sparkles, Send, AlertCircle, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

export default function AskAIPage() {
  const [question, setQuestion] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [deletingHistoryId, setDeletingHistoryId] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  // Function to remove markdown syntax while preserving structure
  const stripMarkdown = (text) => {
    if (!text) return '';
    return text
      // Remove headers (## Title → Title) but keep the text
      .replace(/^#{1,6}\s+(.+)$/gm, '$1')
      // Remove bold (**text** or __text__ → text)
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/__(.+?)__/g, '$1')
      // Remove italic (*text* or _text_ → text)
      .replace(/\*([^*]+?)\*/g, '$1')
      .replace(/_([^_]+?)_/g, '$1')
      // Remove strikethrough (~~text~~ → text)
      .replace(/~~(.+?)~~/g, '$1')
      // Remove inline code (`code` → code)
      .replace(/`([^`]+?)`/g, '$1')
      // Remove code block markers but keep content
      .replace(/```[\w]*\n/g, '')
      .replace(/```/g, '')
      // Remove link syntax [text](url) → text
      .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
      // Convert bullet points to bullet symbol (preserve structure)
      .replace(/^\s*[-*+]\s+/gm, '• ')
      // Keep numbered lists as-is (don't remove them)
      // Remove blockquote markers but keep text
      .replace(/^>\s+/gm, '')
      // Remove horizontal rules
      .replace(/^[-*_]{3,}$/gm, '')
      // Keep spacing - only normalize excessive blank lines
      .replace(/\n{4,}/g, '\n\n\n')
      .trim();
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get('/ai/history');
      setHistory(response.data);
    } catch (error) {
      console.warn('AI history fetch skipped:', error.message);
    }
  };

  const deleteHistoryItem = async (e, chatId) => {
    e.stopPropagation(); // Prevent card click event
    
    setDeletingHistoryId(chatId);
    try {
      await api.delete(`/ai/history/${chatId}`);
      toast.success('Question deleted');
      fetchHistory(); // Refresh the list
    } catch (error) {
      toast.error('Failed to delete question');
    } finally {
      setDeletingHistoryId(null);
    }
  };

  const askQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    setLoading(true);
    const userQuestion = question;
    setQuestion('');

    // Add user message to chat
    setChatHistory((prev) => [...prev, { type: 'user', content: userQuestion }]);

    try {
      const response = await api.post('/ai/ask', { question: userQuestion });
      setChatHistory((prev) => [...prev, { type: 'ai', content: response.data.answer }]);
      fetchHistory();
    } catch (error) {
      toast.error('Failed to get AI response');
      setChatHistory((prev) => [
        ...prev,
        { type: 'error', content: 'Sorry, I encountered an error. Please try again.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
            Ask AI Assistant
          </h1>
          <p className="text-slate-600 dark:text-slate-400">Get instant answers to procurement law questions</p>
        </div>

        {/* Disclaimer */}
        <Alert className="mb-6 border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30">
          <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <AlertDescription className="text-amber-800 dark:text-amber-200">
            <strong>Disclaimer:</strong> AI responses are for informational purposes only and do not constitute legal advice. Always consult official sources and legal professionals for authoritative guidance.
          </AlertDescription>
        </Alert>

        {/* Chat Area */}
        <div className="space-y-4 mb-6">
          {chatHistory.length === 0 ? (
            <Card className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white border-0 shadow-xl">
              <CardContent className="p-8 text-center">
                <Sparkles className="w-16 h-16 mx-auto mb-4 text-emerald-100" />
                <p className="text-white text-lg mb-2">Ask me anything about Philippine Procurement Law</p>
                <p className="text-sm text-emerald-100">Try: "What is the threshold for public bidding?"</p>
              </CardContent>
            </Card>
          ) : (
            chatHistory.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`chat-message-${index}`}
              >
                <Card
                  className={`max-w-[80%] ${
                    message.type === 'user'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : message.type === 'error'
                      ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
                      : 'bg-white dark:bg-slate-800 dark:border-slate-700'
                  }`}
                >
                  <CardContent className="p-4">
                    <p className={`whitespace-pre-wrap ${message.type === 'error' ? 'dark:text-red-200' : message.type !== 'user' ? 'dark:text-slate-200' : ''}`}>
                      {message.type === 'ai' ? stripMarkdown(message.content) : message.content}
                    </p>
                  </CardContent>
                </Card>
              </div>
            ))
          )}

          {loading && (
            <div className="flex justify-start">
              <Card className="max-w-[80%] bg-white dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                    <div className="animate-pulse">Smart-scanning my sources for you, please wait...</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Input Form */}
        <form onSubmit={askQuestion} className="flex gap-2">
          <Textarea
            placeholder="Ask your procurement law question..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
            className="flex-1 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
            data-testid="ai-question-input"
            disabled={loading}
          />
          <Button
            type="submit"
            className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 h-auto"
            disabled={loading || !question.trim()}
            data-testid="send-question-btn"
          >
            <Send className="w-5 h-5" />
          </Button>
        </form>

        {/* Recent History */}
        {history.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'IBM Plex Sans' }}>
              Recent Questions
            </h2>
            <div className="space-y-3">
              {history.slice(0, 5).map((item) => (
                <Card
                  key={item.id}
                  className="group cursor-pointer hover:shadow-md transition-all dark:bg-slate-800 dark:border-slate-700"
                  onClick={() => {
                    setChatHistory([
                      { type: 'user', content: item.question },
                      { type: 'ai', content: item.answer },
                    ]);
                  }}
                  data-testid={`history-item-${item.id}`}
                >
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-1 flex-1">
                      {item.question}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => deleteHistoryItem(e, item.id)}
                      disabled={deletingHistoryId === item.id}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0"
                    >
                      {deletingHistoryId === item.id ? (
                        <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
